import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Trash2, UserPlus, ShieldCheck } from "lucide-react";

export default function Admin() {
  const [comps, setComps] = useState([]);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ email: "", tier: "pro", note: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [c, u] = await Promise.all([
      api.get("/api/admin/comps").then((r) => r.data),
      api.get("/api/admin/users").then((r) => r.data),
    ]);
    setComps(c);
    setUsers(u);
  };

  useEffect(() => { load().catch(() => toast.error("Admin access required")); }, []);

  const addComp = async (e) => {
    e.preventDefault();
    if (!form.email) return;
    setBusy(true);
    try {
      const { data } = await api.post("/api/admin/comps", form);
      if (data.status === "existing_user_upgraded") {
        toast.success(`Upgraded ${form.email} to ${form.tier}`);
      } else {
        toast.success(`Comp created — ${form.email} can now register free at ${form.tier}`);
      }
      setForm({ email: "", tier: "pro", note: "" });
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const removeComp = async (email) => {
    if (!window.confirm(`Remove comp for ${email}?`)) return;
    try {
      await api.delete(`/api/admin/comps/${encodeURIComponent(email)}`);
      toast.success("Removed");
      await load();
    } catch (err) { toast.error("Failed"); }
  };

  return (
    <div className="min-h-screen">
      <div className="border-b border-border">
        <div className="px-8 py-8 flex items-center gap-3">
          <ShieldCheck className="h-5 w-5 text-primary"/>
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Admin · Comp management</div>
            <h1 className="mt-1 font-display font-black text-4xl tracking-tighter">Grant free access.</h1>
          </div>
        </div>
      </div>

      <div className="p-8 grid grid-cols-12 gap-4">
        <form onSubmit={addComp} className="col-span-12 lg:col-span-5 border border-border rounded p-6 bg-card space-y-4" data-testid="comp-form">
          <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Add / upgrade a user</div>
          <div>
            <Label className="text-xs uppercase tracking-widest">Email</Label>
            <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-11 mt-1" type="email" required data-testid="comp-email"/>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest">Tier</Label>
            <Select value={form.tier} onValueChange={(v) => setForm({ ...form, tier: v })}>
              <SelectTrigger className="h-11 mt-1" data-testid="comp-tier"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="creator">Creator</SelectItem>
                <SelectItem value="pro">Pro</SelectItem>
                <SelectItem value="team">Team</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest">Note (optional)</Label>
            <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="h-11 mt-1" placeholder="e.g. beta tester, refund replacement" data-testid="comp-note"/>
          </div>
          <Button type="submit" disabled={busy} className="w-full h-11" data-testid="btn-add-comp">
            <UserPlus className="h-4 w-4"/> {busy ? "Working…" : "Grant free access"}
          </Button>
          <div className="text-xs text-muted-foreground pt-2 border-t border-border">
            If the email is not registered yet, we create a pending comp — that address can then sign up on <span className="font-mono">/create-profile</span> without paying. If they already have an account, we upgrade them in place.
          </div>
        </form>

        <div className="col-span-12 lg:col-span-7 space-y-4">
          <div className="border border-border rounded bg-card" data-testid="comps-list">
            <div className="p-4 border-b border-border font-mono text-xs uppercase tracking-widest text-muted-foreground">Pending comps ({comps.length})</div>
            {comps.length === 0 ? (
              <div className="p-6 text-sm text-muted-foreground">No pending comps.</div>
            ) : (
              <div className="divide-y divide-border">
                {comps.map((c) => (
                  <div key={c.email} className="p-4 flex items-center justify-between gap-3" data-testid={`comp-row-${c.email}`}>
                    <div className="min-w-0">
                      <div className="font-semibold truncate">{c.email}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        <span className="pillar-badge mr-2">{c.tier}</span>
                        {c.note && <span>· {c.note}</span>}
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => removeComp(c.email)} data-testid={`btn-remove-comp-${c.email}`}>
                      <Trash2 className="h-3.5 w-3.5"/>
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="border border-border rounded bg-card" data-testid="users-list">
            <div className="p-4 border-b border-border font-mono text-xs uppercase tracking-widest text-muted-foreground">All users ({users.length})</div>
            <div className="max-h-[480px] overflow-auto divide-y divide-border">
              {users.map((u) => (
                <div key={u.id} className="p-3 flex items-center justify-between gap-3 text-sm">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate">{u.name} <span className="text-muted-foreground font-normal">· {u.email}</span></div>
                    <div className="text-xs text-muted-foreground mt-0.5 flex gap-2 flex-wrap">
                      {u.plan && <span className="pillar-badge text-primary border-primary/40">{u.plan}</span>}
                      {u.plan_status && <span className="pillar-badge">{u.plan_status}</span>}
                      {u.is_admin && <span className="pillar-badge text-destructive border-destructive/30">admin</span>}
                      {u.onboarded && <span className="pillar-badge">onboarded</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
